import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  getBacktest,
  listBacktestTrades,
  MAX_TRADES_PER_PAGE,
  type BacktestTrade,
} from "../api/backtests";
import {
  getBars,
  getTradedBase,
  type BarsResponse,
  type TradedBaseResponse,
} from "../api/bars";
import { getStrategy } from "../api/strategies";
import Card from "../components/Card";
import PriceChart from "../components/PriceChart";
import Button from "../components/Button";
import IndicatorsPanel from "../components/IndicatorsPanel";
import BacktestBalanceCurve from "../components/BacktestBalanceCurve";
import PnlCalendar from "../components/PnlCalendar";
import {
  buildInstanceFromSpec,
  type IndicatorInstance,
} from "../lib/indicators";
import { isGoldenFamily } from "../lib/goldenFamily";

export default function BacktestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ["backtest", id],
    queryFn: () => getBacktest(id!),
    enabled: !!id,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === "PENDING" || status === "RUNNING" ? 2_000 : false;
    },
  });

  // Fetch the strategy metadata (for default_indicators).
  const strategyDetail = useQuery({
    queryKey: ["strategy", data?.strategy_key],
    queryFn: () => getStrategy(data!.strategy_key),
    enabled: !!data?.strategy_key,
  });

  // Indicator state: starts empty, gets seeded with the strategy's defaults
  // once both the strategy + the backtest have loaded.
  const [indicators, setIndicators] = useState<IndicatorInstance[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [editingIndicator, setEditingIndicator] =
    useState<IndicatorInstance | null>(null);
  useEffect(() => {
    if (seeded || !data || !strategyDetail.data) return;
    const seed: IndicatorInstance[] = [];
    for (const spec of strategyDetail.data.default_indicators) {
      const inst = buildInstanceFromSpec(
        {
          type: spec.type,
          params: spec.params,
          pane: spec.pane,
          color: spec.color ?? undefined,
          label: spec.label ?? undefined,
        },
        data.strategy_params,
      );
      if (inst) seed.push(inst);
    }
    setIndicators(seed);
    setSeeded(true);
  }, [data, strategyDetail.data, seeded]);

  // Option-routed runs never trade the symbol on the run row — the engine
  // stitches the near-month future (or the index) as its base. Chart THAT.
  const routing = (data?.strategy_params?.__routing__ ?? null) as {
    order_instrument?: string;
    base_instrument?: string;
    option_underlying?: string;
  } | null;
  const isOptionRouted = routing?.order_instrument === "option";
  const isDerivative = ["FUT", "CE", "PE"].includes(data?.instrument_type ?? "");

  // Fetch the candles for what the backtest actually ran on. Only run when
  // the backtest has finished and we have window metadata.
  const bars = useQuery({
    queryKey: ["bars", data?.id, isOptionRouted],
    queryFn: async (): Promise<
      BarsResponse & Partial<Pick<TradedBaseResponse, "segments" | "root" | "base_instrument">>
    > => {
      if (isOptionRouted) {
        return getTradedBase({
          symbol: data!.symbol,
          timeframe: data!.timeframe,
          start: data!.start_ts + "Z",
          end: data!.end_ts + "Z",
          base_instrument:
            routing?.base_instrument === "index" ? "index" : "futures",
          underlying: routing?.option_underlying || undefined,
        });
      }
      return getBars({
        symbol: data!.symbol,
        exchange: data!.exchange,
        timeframe: data!.timeframe,
        instrument_type: data!.instrument_type,
        // A stray expiry on a non-derivative run row (old bug) would build a
        // bars key that matches nothing — only send it where it belongs.
        expiry: isDerivative ? data!.expiry : null,
        strike: isDerivative ? data!.strike : null,
        lot_size: data!.lot_size,
        tick_size: data!.tick_size,
        start: data!.start_ts + "Z",
        end: data!.end_ts + "Z",
      });
    },
    enabled:
      !!data &&
      (data.status === "SUCCESS" ||
        data.status === "FAILED" ||
        data.status === "CANCELLED"),
  });
  const baseSegments = bars.data?.segments;

  // Every trade, for the chart markers and the P&L calendar — both of which
  // genuinely need the whole set, unlike the trades TABLE, which pages.
  //
  // This is deliberately a SECOND request rather than part of the detail: the
  // summary, metrics and config now paint the moment `getBacktest` returns,
  // and this loads alongside the (already heavy) bars fetch instead of in
  // front of it. It stays idle while a run is still in flight, when there is
  // nothing to draw yet.
  const allTrades = useQuery({
    queryKey: ["backtest-trades-all", data?.id],
    queryFn: () =>
      listBacktestTrades(data!.id, { limit: MAX_TRADES_PER_PAGE }),
    enabled:
      !!data && data.status !== "PENDING" && data.status !== "RUNNING",
    staleTime: Infinity, // a finished run's trades never change
  });
  const trades: BacktestTrade[] = allTrades.data?.items ?? [];

  // Golden Number Breakout: per-day golden-number level for the chart. For
  // any injected source the values come from the backend (finest-TF); for
  // `manual` we draw the constant param across every session in the loaded
  // bars. Undefined for any other strategy (no golden line).
  const goldenByDay = useMemo<Record<string, number> | undefined>(() => {
    if (!data || !isGoldenFamily(data.strategy_key)) return undefined;
    const params = (data.strategy_params ?? {}) as Record<string, unknown>;
    const map: Record<string, number> = {};
    if (params.golden_source === "manual") {
      const val = Number(params.golden_number);
      if (!Number.isFinite(val) || !bars.data) return undefined;
      for (const b of bars.data.bars) map[istDayOf(b.ts)] = val;
    } else {
      for (const g of data.golden_numbers ?? []) {
        const v = Number(g.value);
        if (Number.isFinite(v)) map[g.day] = v;
      }
    }
    return Object.keys(map).length ? map : undefined;
  }, [data, bars.data]);

  // Value area (VAH/VAL) for runs that use it. The backend only populates
  // this when flip_on_value_area / ladders_on_value_area is set, so an empty
  // list means "this run does not use it", not "we failed to compute it".
  const valueAreaByDay = useMemo<
    Record<string, { vah: number; val: number }> | undefined
  >(() => {
    if (!data || !isGoldenFamily(data.strategy_key)) return undefined;
    const map: Record<string, { vah: number; val: number }> = {};
    for (const v of data.value_area ?? []) {
      const vah = Number(v.vah);
      const val = Number(v.val);
      if (Number.isFinite(vah) && Number.isFinite(val)) {
        map[v.day] = { vah, val };
      }
    }
    return Object.keys(map).length ? map : undefined;
  }, [data]);

  // Does this run place its rungs on the value-area edges?
  const valueAreaLadder = useMemo<boolean>(() => {
    const p = (data?.strategy_params ?? {}) as Record<string, unknown>;
    return p.ladders_on_value_area === true;
  }, [data]);

  // Golden Number Breakout ladder offsets (points), drawn as dotted levels
  // golden ± each offset. Undefined for other strategies.
  const ladderPoints = useMemo<number[] | undefined>(() => {
    if (!data || !isGoldenFamily(data.strategy_key)) return undefined;
    const lp = (data.strategy_params as Record<string, unknown>)?.ladder_points;
    if (!Array.isArray(lp)) return undefined;
    const nums = lp.map(Number).filter((n) => Number.isFinite(n) && n > 0);
    return nums.length ? nums : undefined;
  }, [data]);

  // Fullscreen plumbing for the chart card body. Must be declared BEFORE any
  // early return so React sees a stable hook order across renders.
  // lightweight-charts' autoSize picks up the new dimensions when the wrapper
  // grows.
  const chartFullscreenRef = useRef<HTMLDivElement | null>(null);
  const [isChartFullscreen, setIsChartFullscreen] = useState(false);
  useEffect(() => {
    function handler() {
      setIsChartFullscreen(
        document.fullscreenElement === chartFullscreenRef.current,
      );
    }
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);
  function toggleChartFullscreen() {
    const el = chartFullscreenRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }

  if (isLoading) return <div className="text-sm text-muted">Loading…</div>;
  if (error || !data)
    return (
      <div className="text-sm text-down-700 dark:text-down-400">
        Couldn't load backtest. <Link to="/backtests" className="underline">Back</Link>
      </div>
    );

  const r = data;
  const inFlight = r.status === "PENDING" || r.status === "RUNNING";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/backtests" className="text-sm text-up-700 dark:text-up-400 hover:underline">
            ← All backtests
          </Link>
          <h1 className="text-2xl font-semibold mt-1">
            {r.strategy_key}{" "}
            <span className="text-base font-mono text-muted">
              {r.exchange}:{r.symbol} {r.timeframe}
            </span>
          </h1>
          <div className="text-xs font-mono text-fg-subtle mt-1">
            <span className="text-muted">params:</span>{" "}
            {formatParams(r.strategy_params)}
            <span className="text-muted mx-2">|</span>
            <span className="text-muted">{r.sizing_mode}:</span>{" "}
            {formatParams(r.sizing_params)}
          </div>
          <div className="text-xs text-muted font-mono mt-0.5">
            id={r.id}
          </div>
        </div>
        <StatusBlock status={r.status} message={r.error_message} />
      </div>

      <Card title="Summary">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric
            label="Initial capital"
            value={
              r.margin && r.margin !== "1"
                ? `${r.initial_capital} (× ${r.margin})`
                : r.initial_capital
            }
          />
          <Metric
            label="Final equity"
            value={num(r.final_equity)}
            mono
          />
          <Metric
            label="Net P&L"
            value={num(r.net_pnl)}
            mono
            tone={tone(r.net_pnl)}
          />
          <Metric
            label="Return"
            value={pct(r.return_pct)}
            tone={tone(r.return_pct)}
          />
          <Metric label="Max drawdown" value={pct(r.max_drawdown_pct)} tone="bad" />
          <Metric label="Sharpe (annual.)" value={num(r.sharpe, 2)} />
          <Metric label="Closing trades" value={r.closing_trades?.toString() ?? "—"} />
          <Metric label="Win rate" value={pct(r.win_rate_pct)} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-xs text-muted">
          <Stat label="Bars processed" value={r.bars_processed?.toLocaleString() ?? "—"} />
          <Stat label="Orders submitted" value={r.orders_submitted?.toString() ?? "—"} />
          <Stat label="Orders rejected" value={r.orders_rejected?.toString() ?? "—"} />
          <Stat label="Fills" value={r.fills_count?.toString() ?? "—"} />
        </div>
      </Card>

      {r.regime_report && (
        <Card title="Regime analysis">
          <p className="mb-4 text-sm text-muted">
            The same run, re-scored inside slices of the market it traded
            through. Each table splits the backtest a different way and
            recomputes the metrics using only the bars in that slice — so you
            can see <em>where</em> the P&amp;L came from rather than just how
            much there was.
          </p>
          <div className="space-y-6">
            <RegimeTable
              title="By year"
              caption="Calendar years. The plainest check for a result that rests on one good year."
              rows={r.regime_report.by_year}
            />
            <RegimeTable
              title={`By trend (SMA ${r.regime_report.trend_window_days}d)`}
              caption={`Each day is labelled from where price sat against its ${r.regime_report.trend_window_days}-day average that morning — bull above, bear below. Days inside the first ${r.regime_report.trend_window_days} days have no average yet and fall in "warmup".`}
              rows={r.regime_report.by_trend}
            />
            <RegimeTable
              title={`By volatility (${r.regime_report.vol_window_days}d, median split)`}
              caption={`Daily-return volatility over ${r.regime_report.vol_window_days} days, split at this run's own median — so "high" and "low" are relative to this window, not an absolute level, and the split is descriptive rather than something you could have traded on.`}
              rows={r.regime_report.by_vol}
            />
          </div>
          <div className="mt-4 space-y-1 text-xs text-muted">
            <p>
              <strong className="text-foreground">Time</strong> — share of the
              run's bars in that slice.{" "}
              <strong className="text-foreground">Return</strong> — compounded
              only over those bars.{" "}
              <strong className="text-foreground">Max DD</strong> — worst
              drawdown within contiguous stretches of the slice, not across
              gaps between them.
            </p>
            <p>
              A robust strategy earns across slices. Returns concentrated in one
              year or one regime point to regime-dependence rather than a
              durable edge — and a slice holding only a handful of trades is
              noise, so read <strong className="text-foreground">Trades</strong>{" "}
              before trusting its Return.
            </p>
          </div>
        </Card>
      )}

      <Card
        title="Price + trades"
        actions={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={toggleChartFullscreen}
            title="Press Esc to exit fullscreen"
          >
            {isChartFullscreen ? "× Exit fullscreen" : "⛶ Fullscreen"}
          </Button>
        }
      >
        <div
          ref={chartFullscreenRef}
          className={isChartFullscreen ? "bg-surface p-4 h-screen w-screen flex flex-col" : ""}
        >
          {inFlight ? (
            <div className="text-sm text-muted">Run is still in progress…</div>
          ) : bars.isLoading ? (
            <div className="text-sm text-muted">Loading bars…</div>
          ) : bars.error || !bars.data ? (
            <div className="text-sm text-down-700 dark:text-down-400">
              Couldn't load price history. The backfill for this instrument may
              be missing.
            </div>
          ) : bars.data.bars.length === 0 ? (
            <div className="text-sm text-muted">
              No bars in the store for this instrument/window. Run a backfill
              on the Data page to populate it, then refresh.
            </div>
          ) : (
            <>
              <IndicatorsPanel
                indicators={indicators}
                onAdd={(inst) => setIndicators((cur) => [...cur, inst])}
                onRemove={(id) =>
                  setIndicators((cur) => cur.filter((i) => i.id !== id))
                }
                onEdit={(inst) => setEditingIndicator(inst)}
              />
              {isOptionRouted && (
                <div className="text-xs text-info-700 dark:text-info-400 bg-info-50 dark:bg-info-500/10 border border-info-200 dark:border-info-400/30 rounded px-3 py-2 my-2">
                  <span className="font-medium">
                    Chart shows the base the backtest actually traded on
                  </span>{" "}
                  — {bars.data.base_instrument === "index"
                    ? `${bars.data.root ?? r.symbol} cash index`
                    : "auto-rolled near-month futures (a different contract each month)"}
                  . Orders were routed to weekly/monthly options; markers show
                  the option fills at the base candle they happened in.
                  {baseSegments && baseSegments.length > 1 && (
                    <span className="block mt-1 font-mono text-[11px] leading-5">
                      {baseSegments.map((s) => (
                        <span key={s.symbol + s.start_ts} className="mr-3 whitespace-nowrap">
                          {s.symbol} ({s.start_ts.slice(0, 10)} → {s.end_ts.slice(0, 10)})
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              )}
              <CoverageBanner
                bars={bars.data.bars}
                requestedStart={r.start_ts}
                requestedEnd={r.end_ts}
              />
              {bars.data.downsampled && (
                <div className="text-xs text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-200 dark:border-warn-400/30 rounded px-3 py-2 my-2">
                  Showing {bars.data.bars.length.toLocaleString()} buckets of{" "}
                  {bars.data.bucket_size} bars each (collapsed from{" "}
                  {bars.data.original_count.toLocaleString()} source bars).
                  Zoom in by querying a narrower window for full resolution.
                </div>
              )}
              <div className={isChartFullscreen ? "flex-1 min-h-0" : ""}>
                <PriceChart
                  bars={bars.data.bars}
                  trades={trades}
                  indicators={indicators}
                  onIndicatorChange={(id, patch) =>
                    setIndicators((cur) =>
                      cur.map((i) => (i.id === id ? { ...i, ...patch } : i)),
                    )
                  }
                  editingIndicator={editingIndicator}
                  onCloseIndicatorEdit={() => setEditingIndicator(null)}
                  watermark={
                    isOptionRouted
                      ? bars.data.base_instrument === "index"
                        ? `${bars.data.root ?? r.symbol} (index base)`
                        : `${bars.data.root ?? r.symbol} near-month (auto-roll)`
                      : `${r.exchange}:${r.symbol}`
                  }
                  timeframe={r.timeframe}
                  instrument={{
                    symbol: r.symbol,
                    exchange: r.exchange,
                    instrument_type: r.instrument_type,
                  }}
                  heightClass={isChartFullscreen ? "h-full" : "h-[480px]"}
                  goldenByDay={goldenByDay}
                  ladderPoints={ladderPoints}
                  valueAreaByDay={valueAreaByDay}
                  valueAreaLadder={valueAreaLadder}
                />
              </div>
            </>
          )}
        </div>
      </Card>

      {!inFlight && trades.length > 0 && (
        <Card title="Daily P&L calendar">
          <PnlCalendar trades={trades} startTs={r.start_ts} endTs={r.end_ts} />
        </Card>
      )}

      {!inFlight && trades.length > 0 && (
        <Card title="Account balance">
          <BacktestBalanceCurve
            trades={trades}
            initialCapital={Number(r.initial_capital)}
          />
        </Card>
      )}

      <Card title="Run config">
        <ConfigBlock data={r} />
      </Card>

      <Card title="Trades">
        <TradesTable runId={r.id} total={r.trades_count} />
      </Card>
    </div>
  );
}

const TRADES_PAGE_SIZE = 25;

/** The trades table, fetching 25 rows at a time.
 *
 *  `total` comes from the run row, so the pager is correct before the first
 *  page has landed and the table never has to hold 40k rows to know how many
 *  there are. */
function TradesTable({ runId, total }: { runId: string; total: number }) {
  const [page, setPage] = useState(0);
  const pageSize = TRADES_PAGE_SIZE;
  const { data, isLoading } = useQuery({
    queryKey: ["backtest-trades", runId, page],
    queryFn: () =>
      listBacktestTrades(runId, {
        limit: pageSize,
        offset: page * pageSize,
      }),
    enabled: total > 0,
    // Paging must not blank the table between requests.
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  if (total === 0) return <div className="text-sm text-muted">No fills.</div>;
  if (isLoading && !data)
    return <div className="text-sm text-muted">Loading trades…</div>;

  const visible = data?.items ?? [];
  // The server's count wins once it has answered — `total` off the run row is
  // there so the pager works on the very first render.
  const count = data?.total ?? total;
  const start = page * pageSize;
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  return (
    <div className="space-y-2">
      <table className="w-full text-sm">
        <thead className="text-left text-muted border-b border-border/60">
          <tr>
            <th className="py-1 pr-3">#</th>
            <th className="py-1 pr-3">Timestamp</th>
            <th className="py-1 pr-3">Side</th>
            <th className="py-1 pr-3">Symbol</th>
            <th className="py-1 pr-3 text-right">Qty</th>
            <th className="py-1 pr-3 text-right">Price</th>
            <th className="py-1 pr-3 text-right">Fees</th>
            <th className="py-1 pr-3 text-right">Realized Δ</th>
            <th className="py-1 pr-3">Tag</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((t) => (
            <tr key={t.idx} className="border-b border-border/60 last:border-0">
              <td className="py-1 pr-3 font-mono text-xs text-muted">{t.idx}</td>
              <td className="py-1 pr-3 font-mono text-xs">
                {formatTradeTs(t.ts)}
              </td>
              <td
                className={`py-1 pr-3 font-mono text-xs ${
                  t.side.toUpperCase() === "BUY"
                    ? "text-up-700 dark:text-up-400"
                    : "text-down-700 dark:text-down-400"
                }`}
              >
                {t.side}
              </td>
              <td className="py-1 pr-3 font-mono">{t.symbol}</td>
              <td className="py-1 pr-3 font-mono text-right">{t.quantity}</td>
              <td className="py-1 pr-3 font-mono text-right">{t.price}</td>
              <td className="py-1 pr-3 font-mono text-right text-xs text-muted">
                {t.fees}
              </td>
              <td
                className={`py-1 pr-3 font-mono text-right ${
                  Number(t.realized_pnl_delta) > 0
                    ? "text-up-700 dark:text-up-400"
                    : Number(t.realized_pnl_delta) < 0
                      ? "text-down-700 dark:text-down-400"
                      : "text-muted"
                }`}
              >
                {t.realized_pnl_delta}
              </td>
              <td
                className="py-1 pr-3 font-mono text-xs text-fg-subtle"
                title={t.tag ?? ""}
              >
                {t.tag ?? ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-fg-subtle">
          <span>
            Showing {start + 1}–{start + visible.length} of{" "}
            {count.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="px-2 py-1 border border-border rounded disabled:opacity-50"
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="px-2 py-1 border border-border rounded disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigBlock({
  data,
}: {
  data: {
    strategy_key: string;
    strategy_params: Record<string, unknown>;
    sizing_mode: string;
    sizing_params: Record<string, unknown>;
    timeframe: string;
    start_ts: string;
    end_ts: string;
    initial_capital: string;
    slippage_bps: number;
    latency_ms: number;
    lot_size: number;
    tick_size: string;
    expiry: string | null;
    strike: string | null;
  };
}) {
  return (
    <div className="grid grid-cols-2 gap-4 text-xs">
      <Stat label="Strategy" value={data.strategy_key} mono />
      <Stat label="Sizer" value={data.sizing_mode} mono />
      <Stat
        label="Strategy params"
        value={
          <pre className="bg-surface-2 border border-border rounded p-2 font-mono text-[11px] whitespace-pre-wrap">
            {JSON.stringify(data.strategy_params, null, 2)}
          </pre>
        }
      />
      <Stat
        label="Sizing params"
        value={
          <pre className="bg-surface-2 border border-border rounded p-2 font-mono text-[11px] whitespace-pre-wrap">
            {JSON.stringify(data.sizing_params, null, 2)}
          </pre>
        }
      />
      <Stat label="Timeframe" value={data.timeframe} mono />
      <Stat
        label="Window"
        value={`${new Date(data.start_ts + "Z").toLocaleString()} → ${new Date(
          data.end_ts + "Z",
        ).toLocaleString()}`}
      />
      <Stat label="Slippage (bps)" value={data.slippage_bps.toString()} mono />
      <Stat label="Latency (ms)" value={data.latency_ms.toString()} mono />
      <Stat label="Lot size" value={data.lot_size.toString()} mono />
      <Stat label="Tick size" value={data.tick_size} mono />
      {data.expiry && <Stat label="Expiry" value={data.expiry} mono />}
      {data.strike && <Stat label="Strike" value={data.strike} mono />}
    </div>
  );
}

function StatusBlock({
  status,
  message,
}: {
  status: string;
  message: string | null;
}) {
  const cls =
    status === "SUCCESS"
      ? "bg-up-50 dark:bg-up-500/10 border-up-200 dark:border-up-400/30 text-up-700 dark:text-up-400"
      : status === "FAILED"
        ? "bg-down-50 dark:bg-down-500/10 border-down-200 dark:border-down-400/30 text-down-700 dark:text-down-400"
        : status === "CANCELLED"
          ? "bg-surface-2 border-border text-fg-subtle"
          : "bg-warn-50 dark:bg-warn-500/10 border-warn-200 dark:border-warn-400/30 text-warn-700 dark:text-warn-400";
  return (
    <div className={`border rounded px-3 py-2 ${cls}`}>
      <div className="font-mono text-xs uppercase tracking-wide">{status}</div>
      {message && <div className="text-xs mt-1 max-w-md">{message}</div>}
    </div>
  );
}

function Metric({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "good" | "bad" | undefined;
}) {
  const valueCls =
    tone === "good"
      ? "text-up-700 dark:text-up-400"
      : tone === "bad"
        ? "text-down-700 dark:text-down-400"
        : "text-fg";
  return (
    <div>
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className={`text-lg ${mono ? "font-mono" : ""} ${valueCls}`}>
        {value}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-muted">{label}</div>
      <div className={`${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function pct(v: string | null): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return `${n.toFixed(2)}%`;
}

function num(v: string | null, digits = 2): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return n.toFixed(digits);
}

/**
 * Compare the bars we got from the store to the backtest's requested window.
 * If either end is short by more than ~1 bar's worth of time, surface a
 * banner so the user understands why the chart starts where it does and
 * what to do about it.
 *
 * The threshold uses 1 day rather than e.g. one bar duration because a small
 * gap at the boundary (a missing tail or a tick rounding) is normal noise and
 * not worth alerting on. Real backfill gaps look like weeks/months.
 */
function CoverageBanner({
  bars,
  requestedStart,
  requestedEnd,
}: {
  bars: { ts: string }[];
  requestedStart: string;
  requestedEnd: string;
}) {
  if (bars.length === 0) return null;
  const reqStart = parseTs(requestedStart);
  const reqEnd = parseTs(requestedEnd);
  const actualStart = parseTs(bars[0].ts);
  const actualEnd = parseTs(bars[bars.length - 1].ts);
  // Bail if any parse failed (renders nothing rather than crashing on
  // `new Date(NaN).toISOString()` further down).
  if (
    !Number.isFinite(reqStart) ||
    !Number.isFinite(reqEnd) ||
    !Number.isFinite(actualStart) ||
    !Number.isFinite(actualEnd)
  ) {
    return null;
  }
  const ONE_DAY = 86_400_000;
  const startGap = actualStart - reqStart;
  const endGap = reqEnd - actualEnd;
  if (startGap <= ONE_DAY && endGap <= ONE_DAY) return null;

  const totalSpan = Math.max(1, reqEnd - reqStart);
  const coveredSpan = Math.max(0, actualEnd - actualStart);
  const pctCovered = ((coveredSpan / totalSpan) * 100).toFixed(1);

  return (
    <div className="text-xs text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-amber-300 dark:border-warn-400/30 rounded px-3 py-2 my-2 space-y-1">
      <div className="font-medium">
        Data gap — the store doesn't cover the full backtest window
      </div>
      <div>
        Requested:{" "}
        <span className="font-mono">
          {fmtDate(reqStart)} → {fmtDate(reqEnd)}
        </span>
      </div>
      <div>
        Available in store:{" "}
        <span className="font-mono">
          {fmtDate(actualStart)} → {fmtDate(actualEnd)}
        </span>{" "}
        ({pctCovered}% of window)
      </div>
      <div className="text-warn-700 dark:text-warn-400">
        The backtest only used bars that existed at run time.{" "}
        <Link to="/data" className="underline font-medium">
          Run a backfill
        </Link>{" "}
        for the missing range, then re-run this backtest to use the full
        window.
      </div>
    </div>
  );
}

/**
 * Parse an ISO timestamp from the backend. The backend may emit either
 * naive UTC (no suffix — e.g. backtest start_ts/end_ts) or timezone-aware
 * (e.g. Delta India bars carry `+05:30`). Append "Z" only when the string
 * has no existing TZ indicator; otherwise `new Date("…+05:30Z")` is NaN.
 */
function parseTs(ts: string): number {
  if (!ts) return NaN;
  const hasTz = ts.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(ts);
  return new Date(hasTz ? ts : `${ts}Z`).getTime();
}

/** IST calendar day ("YYYY-MM-DD") of a bar timestamp — matches the backend's
 *  golden-number day keys and the GOLDEN indicator's lookup. */
function istDayOf(ts: string): string {
  return new Date(parseTs(ts) + 19_800_000).toISOString().slice(0, 10);
}

function fmtDate(epoch: number): string {
  if (!Number.isFinite(epoch)) return "?";
  return new Date(epoch).toISOString().slice(0, 10);
}

/** Trade timestamps in the trades table. Includes the day-of-week so the
 *  user can eyeball whether weekly strategies are firing on the right day
 *  (e.g. weekly_htp_revert should enter on Mondays). */
function formatTradeTs(ts: string): string {
  const d = new Date(ts + "Z");
  if (Number.isNaN(d.getTime())) return ts;
  const dow = d.toLocaleDateString("en-US", { weekday: "short" });
  return `${dow} ${d.toLocaleString()}`;
}

function formatParams(p: Record<string, unknown>): string {
  const entries = Object.entries(p);
  if (entries.length === 0) return "(defaults)";
  return entries
    .map(([k, v]) => {
      const valStr =
        v == null
          ? "-"
          : typeof v === "number" ||
              typeof v === "string" ||
              typeof v === "boolean"
            ? String(v)
            : JSON.stringify(v);
      return `${k}=${valStr}`;
    })
    .join(", ");
}

function tone(v: string | null): "good" | "bad" | undefined {
  if (v == null || v === "") return undefined;
  const n = Number(v);
  if (Number.isNaN(n)) return undefined;
  return n > 0 ? "good" : n < 0 ? "bad" : undefined;
}

/** One slice table of the regime analysis card (by year / trend / vol). */
function RegimeTable({
  title,
  caption,
  rows,
}: {
  title: string;
  caption?: string;
  rows: import("../api/backtests").RegimeSliceRow[];
}) {
  if (!rows.length) return null;
  const toneCls = (n: number) =>
    n > 0 ? "text-emerald-500" : n < 0 ? "text-red-500" : "";
  return (
    <div>
      <div className="text-sm font-medium">{title}</div>
      {caption && <p className="mb-2 mt-0.5 text-xs text-muted">{caption}</p>}
      {/* Seven numeric columns never fit a third of the page — full width, and
          scroll on narrow screens rather than letting the headers collide. */}
      <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] text-sm">
        <thead className="text-left text-muted border-b border-border/60">
          <tr>
            <th className="py-1 pr-3">Slice</th>
            <th className="py-1 pr-3 text-right">Time</th>
            <th className="py-1 pr-3 text-right">Return</th>
            <th className="py-1 pr-3 text-right">Sharpe</th>
            <th className="py-1 pr-3 text-right">Max DD</th>
            <th className="py-1 pr-3 text-right">Trades</th>
            <th className="py-1 text-right">Win</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.label} className="border-b border-border/60 last:border-0">
              <td className="py-1 pr-3">{s.label.replace("_", " ")}</td>
              <td className="py-1 pr-3 text-right text-muted">
                {s.time_pct.toFixed(0)}%
              </td>
              <td className={`py-1 pr-3 text-right font-mono ${toneCls(s.return_pct)}`}>
                {s.return_pct.toFixed(2)}%
              </td>
              <td className="py-1 pr-3 text-right font-mono">
                {s.sharpe != null ? s.sharpe.toFixed(2) : "—"}
              </td>
              <td className="py-1 pr-3 text-right font-mono">
                {s.max_drawdown_pct.toFixed(2)}%
              </td>
              <td className="py-1 pr-3 text-right">{s.closing_trades}</td>
              <td className="py-1 text-right">
                {s.win_rate_pct != null ? `${s.win_rate_pct.toFixed(0)}%` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

